import React, { useState, useEffect, useRef } from 'react';
import {
  getPosts, createPost, updatePost, deletePost, uploadImage,
  getSettings, saveSettings, uploadLogo, uploadLogoCircular,
  getUsers, createUser, updateUser, deleteUser,
  getCategories, createCategory, deleteCategory,
  getRedes, createRed, deleteRed,
  login, logout, isLogged
} from '../api';

// ── LOGIN ─────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const res = await login(form.username, form.password);
    setLoading(false);
    if (res.token) { localStorage.setItem('admin_token', res.token); onLogin(); }
    else setError(res.error || 'Credenciales incorrectas');
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'2.5rem', width:'100%', maxWidth:380 }}>
        <h2 style={{ color:'var(--mustard)', marginBottom:'.4rem', letterSpacing:1 }}>369Admin</h2>
        <p style={{ color:'var(--muted)', fontSize:'.9rem', marginBottom:'1.8rem' }}>Panel de administración</p>
        {error && <div style={{ background:'#3a1a1a', border:'1px solid #6a2d2d', color:'#da7d7d', padding:'.6rem .9rem', borderRadius:6, marginBottom:'1rem', fontSize:'.88rem' }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Usuario</label>
            <input autoFocus value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Contraseña</label>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width:'100%', marginTop:'.5rem' }} disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── POST MODAL ────────────────────────────────────────────
function PostModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', video_url: '', images: [], category_id: '' });
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    if (post) setForm({ title: post.title || '', description: post.description || '', video_url: post.video_url || '', images: post.images || [], category_id: post.category_id || '' });
  }, [post]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImages = async (files) => {
    setUploading(true);
    const urls = [];
    for (const file of files) {
      const res = await uploadImage(file);
      if (res.image_url) urls.push(res.image_url);
    }
    setForm(f => ({ ...f, images: [...f.images, ...urls] }));
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title.trim()) return alert('El título es obligatorio');
    if (post?.id) await updatePost(post.id, form);
    else await createPost(form);
    onSaved(); onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{post?.id ? 'Editar publicación' : 'Nueva publicación'}</h2>
        <div className="form-group">
          <label>Título *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Título" />
        </div>
        <div className="form-group">
          <label>Descripción</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descripción..." />
        </div>
        <div className="form-group">
          <label>URL de video (YouTube / Vimeo / Facebook / Instagram / S3)</label>
          <input value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." />
        </div>
        <div className="form-group">
          <label>Categoría</label>
          <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
            style={{ width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', padding:'.6rem .8rem', fontSize:'.95rem' }}>
            <option value="">Sin categoría</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Galería de fotos</label>
          <input type="file" ref={fileRef} multiple accept="image/*" style={{ display:'none' }} onChange={e => handleImages(Array.from(e.target.files))} />
          <button className="btn btn-outline btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? 'Subiendo...' : '+ Agregar fotos'}
          </button>
          <div className="img-preview-list">
            {form.images.map((url, i) => (
              <div key={i} className="img-preview-item">
                <img src={url} alt={`img-${i}`} />
                <button onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}>×</button>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── USER MODAL ────────────────────────────────────────────
function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ username: '', password: '', role: 'editor' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) setForm({ username: user.username, password: '', role: user.role });
  }, [user]);

  const submit = async () => {
    if (!form.username.trim()) return setError('El usuario es obligatorio');
    if (!user?.id && !form.password.trim()) return setError('La contraseña es obligatoria');
    const res = user?.id ? await updateUser(user.id, form) : await createUser(form);
    if (res.error) return setError(res.error);
    onSaved(); onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{user?.id ? 'Editar usuario' : 'Nuevo usuario'}</h2>
        {error && <div style={{ color:'#da7d7d', fontSize:'.88rem', marginBottom:'1rem' }}>{error}</div>}
        <div className="form-group">
          <label>Usuario *</label>
          <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="nombre de usuario" />
        </div>
        <div className="form-group">
          <label>{user?.id ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
          <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Rol</label>
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            style={{ width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', padding:'.6rem .8rem', fontSize:'.95rem' }}>
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
          </select>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS ──────────────────────────────────────────────
function SettingsPanel({ onSaved }) {
  const [form, setForm] = useState({
    site_title: '',
    logo_url: '',
    logo_circular_url: '',
    logo_size: 52,
    color_header: '#b81623',
    color_bg: '#000000',
    color_footer: '#111111',
    color_titles: '#b81623',
    color_subtitles: '#a8a8a8',
    site_bio: '',
    footer_text: ''
  });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const logoCircRef = useRef();

  useEffect(() => {
    getSettings().then(s => setForm({
      site_title: s.site_title || '',
      logo_url: s.logo_url || '',
      logo_circular_url: s.logo_circular_url || '',
      logo_size: parseInt(s.logo_size) || 52,
      color_header: s.color_header || '#b81623',
      color_bg: s.color_bg || '#000000',
      color_footer: s.color_footer || '#111111',
      color_titles: s.color_titles || '#b81623',
      color_subtitles: s.color_subtitles || '#a8a8a8',
      site_bio: s.site_bio || 'Producción audiovisual · Creatividad sin límites',
      footer_text: s.footer_text || '© El Popular. Todos los derechos reservados.'
    })).catch(() => {});
  }, []);

  const handleLogo = async (file) => {
    const res = await uploadLogo(file);
    if (res.logo_url) setForm(f => ({ ...f, logo_url: res.logo_url }));
  };

  const handleLogoCircular = async (file) => {
    const res = await uploadLogoCircular(file);
    if (res.logo_circular_url) setForm(f => ({ ...f, logo_circular_url: res.logo_circular_url }));
  };

  const submit = async () => {
    setSaving(true);
    await saveSettings(form);
    setSaving(false);
    onSaved();
    alert('Configuración guardada');
  };

  const change = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  return (
    <div style={{ maxWidth: 580 }}>
      <div className="section-header"><h2>Configuración del sitio</h2></div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div>
          <div className="form-group">
            <label>Título del sitio</label>
            <input value={form.site_title} onChange={e => change('site_title', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Subtítulo / Biografía del perfil</label>
            <textarea value={form.site_bio} onChange={e => change('site_bio', e.target.value)} style={{ minHeight: 80 }} />
          </div>
        </div>
        <div>
          <div className="form-group">
            <label>Texto del Footer</label>
            <textarea value={form.footer_text} onChange={e => change('footer_text', e.target.value)} style={{ minHeight: 140 }} />
          </div>
        </div>
      </div>

      <h3 style={{ margin: '1.5rem 0 .8rem', color: 'var(--mustard)' }}>Personalización de Colores</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem', background: 'var(--surface2)', padding: '1rem', borderRadius: 8, border: '1px solid var(--border)' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Fondo del Header</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <input type="color" value={form.color_header} onChange={e => change('color_header', e.target.value)} style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: '.85rem' }}>{form.color_header}</span>
          </div>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Fondo de la Página</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <input type="color" value={form.color_bg} onChange={e => change('color_bg', e.target.value)} style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: '.85rem' }}>{form.color_bg}</span>
          </div>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Fondo del Footer</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <input type="color" value={form.color_footer} onChange={e => change('color_footer', e.target.value)} style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: '.85rem' }}>{form.color_footer}</span>
          </div>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Color de Títulos</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <input type="color" value={form.color_titles} onChange={e => change('color_titles', e.target.value)} style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: '.85rem' }}>{form.color_titles}</span>
          </div>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Color de Subtítulos</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <input type="color" value={form.color_subtitles} onChange={e => change('color_subtitles', e.target.value)} style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: '.85rem' }}>{form.color_subtitles}</span>
          </div>
        </div>
      </div>

      <h3 style={{ margin: '1.5rem 0 .8rem', color: 'var(--mustard)' }}>Logos e Identidad</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        <div className="form-group">
          <label>Logo principal (navbar)</label>
          <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'.4rem' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => logoRef.current.click()}>Subir logo</button>
            <input type="file" ref={logoRef} accept="image/*" style={{ display:'none' }} onChange={e => handleLogo(e.target.files[0])} />
            {form.logo_url && <img src={form.logo_url} alt="logo" style={{ height:`${Math.min(form.logo_size, 120)}px`, width:'auto', maxWidth:180, objectFit:'contain', border:'1px solid var(--border)', borderRadius:4 }} />}
          </div>
          <div style={{ marginTop: '.8rem' }}>
            <label>Tamaño del logo: <strong style={{ color:'var(--mustard)' }}>{form.logo_size}px</strong></label>
            <input type="range" min="24" max="800" step="4" value={form.logo_size}
              onChange={e => change('logo_size', parseInt(e.target.value))}
              style={{ width:'100%', accentColor:'var(--mustard)', marginTop:'.4rem' }} />
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.75rem', color:'var(--muted)' }}><span>24px</span><span>800px</span></div>
          </div>
        </div>
        
        <div className="form-group">
          <label>Logo circular (avatar del perfil)</label>
          <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'.4rem' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => logoCircRef.current.click()}>Subir logo circular</button>
            <input type="file" ref={logoCircRef} accept="image/*" style={{ display:'none' }} onChange={e => handleLogoCircular(e.target.files[0])} />
            {form.logo_circular_url && <img src={form.logo_circular_url} alt="circular" style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', border:'2px solid var(--mustard)' }} />}
          </div>
        </div>
      </div>

      <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
    </div>
  );
}

// ── CATEGORIES PANEL ──────────────────────────────────────
function CategoriesPanel() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);

  const loadCategories = () => getCategories().then(setCategories).catch(() => {});

  useEffect(() => { loadCategories(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    setLoading(true);
    const res = await createCategory({ name: newCategory.trim() });
    setLoading(false);
    if (res.success) {
      setNewCategory('');
      loadCategories();
    } else {
      alert(res.error || 'Error al crear la categoría');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    await deleteCategory(id);
    loadCategories();
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div className="section-header"><h2>Categorías</h2></div>
      
      <form onSubmit={handleCreate} style={{ display:'flex', gap:'.5rem', marginBottom:'1.5rem' }}>
        <input style={{ flex: 1, background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', padding:'.6rem .8rem', fontSize:'.95rem', outline:'none' }}
          value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Nueva categoría (ej: Cortometrajes)" />
        <button type="submit" className="btn btn-primary" disabled={loading}>Agregar</button>
      </form>

      {categories.length === 0 ? (
        <div className="empty-state">No hay categorías creadas aún.</div>
      ) : (
        <table className="admin-table">
          <thead><tr><th>Nombre</th><th style={{ width:100 }}>Acciones</th></tr></thead>
          <tbody>
            {categories.map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── REDES PANEL ───────────────────────────────────────────
function RedesPanel() {
  const [redes, setRedes] = useState([]);
  const [name, setName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const loadRedes = () => getRedes().then(setRedes).catch(() => {});

  useEffect(() => { loadRedes(); }, []);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const res = await uploadImage(file);
    if (res.image_url) setLogoUrl(res.image_url);
    setUploading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!logoUrl) return alert('El logo circular es obligatorio');
    if (!linkUrl.trim()) return alert('El enlace de la red social es obligatorio');
    setLoading(true);
    const res = await createRed({
      name: name.trim(),
      logo_url: logoUrl,
      link_url: linkUrl.trim()
    });
    setLoading(false);
    if (res.success) {
      setName('');
      setLinkUrl('');
      setLogoUrl('');
      loadRedes();
    } else {
      alert(res.error || 'Error al guardar la red social');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta red social?')) return;
    await deleteRed(id);
    loadRedes();
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="section-header"><h2>Redes Sociales (Highlights)</h2></div>
      
      <form onSubmit={handleCreate} className="form-box" style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
        <h3 style={{ marginBottom: '1rem', color: 'var(--mustard)' }}>+ Agregar Red Social / Enlace</h3>
        
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Nombre / Etiqueta (opcional)</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Instagram, WhatsApp, Facebook" />
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>URL de Enlace *</label>
          <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://instagram.com/elpopular" />
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Logo Redondo / Icono *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '.4rem' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current.click()}>
              {uploading ? 'Subiendo...' : 'Subir imagen circular'}
            </button>
            <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={e => handleLogoUpload(e.target.files[0])} />
            {logoUrl && (
              <img src={logoUrl} alt="Preview" style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border)' }} />
            )}
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
          {loading ? 'Guardando...' : 'Guardar Red Social'}
        </button>
      </form>

      {redes.length === 0 ? (
        <div className="empty-state">No hay redes sociales configuradas aún.</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Icono</th>
              <th>Nombre</th>
              <th>Enlace</th>
              <th style={{ width: 100 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {redes.map(r => (
              <tr key={r.id}>
                <td>
                  <img src={r.logo_url} alt={r.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                </td>
                <td>{r.name || <em style={{ color: 'var(--muted)' }}>Sin etiqueta</em>}</td>
                <td><a href={r.link_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mustard)' }}>{r.link_url}</a></td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── ADMIN PRINCIPAL ───────────────────────────────────────
export default function Admin({ onSettingsChange }) {
  const [logged, setLogged] = useState(isLogged());
  const [tab, setTab]       = useState('posts');
  const [posts, setPosts]   = useState([]);
  const [users, setUsers]   = useState([]);
  const [postModal, setPostModal] = useState(null);
  const [userModal, setUserModal] = useState(null);

  useEffect(() => {
    const originalTitle = document.title;
    let manifest = document.querySelector('link[rel="manifest"]');
    const originalManifest = manifest ? manifest.getAttribute('href') : null;
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const originalAppleTitle = appleTitle ? appleTitle.getAttribute('content') : null;

    document.title = 'EPadmin';
    if (manifest) {
      manifest.setAttribute('href', `${window.__BASE_PATH__ || ''}/manifest-admin.json`);
    }
    if (appleTitle) {
      appleTitle.setAttribute('content', 'EPadmin');
    }

    return () => {
      document.title = originalTitle;
      if (manifest && originalManifest) {
        manifest.setAttribute('href', originalManifest);
      }
      if (appleTitle && originalAppleTitle) {
        appleTitle.setAttribute('content', originalAppleTitle);
      }
    };
  }, []);

  const loadPosts = () => getPosts().then(setPosts).catch(() => {});
  const loadUsers = () => getUsers().then(r => Array.isArray(r) ? setUsers(r) : null).catch(() => {});

  useEffect(() => { if (logged) { loadPosts(); loadUsers(); } }, [logged]);

  const handleLogout = () => { logout(); setLogged(false); };

  const handleDeletePost = async (id) => {
    if (!window.confirm('¿Eliminar esta publicación?')) return;
    await deletePost(id); loadPosts();
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    await deleteUser(id); loadUsers();
  };

  if (!logged) return <LoginScreen onLogin={() => setLogged(true)} />;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div style={{ color:'var(--mustard)', fontWeight:700, fontSize:'1rem', padding:'.6rem .8rem', marginBottom:'.5rem', letterSpacing:1 }}>El Popular Admin</div>
        <button className={tab === 'posts' ? 'active' : ''} onClick={() => setTab('posts')}>📋 Publicaciones</button>
        <button className={tab === 'categories' ? 'active' : ''} onClick={() => setTab('categories')}>🏷️ Categorías</button>
        <button className={tab === 'redes' ? 'active' : ''} onClick={() => setTab('redes')}>🔗 Redes Sociales</button>
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>👥 Usuarios</button>
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>⚙️ Configuración</button>
        <button onClick={handleLogout} style={{ marginTop:'auto', color:'#da7d7d' }}>🚪 Cerrar sesión</button>
      </aside>

      <main className="admin-content">

        {/* PUBLICACIONES */}
        {tab === 'posts' && (
          <>
            <div className="section-header">
              <h2>Publicaciones</h2>
              <button className="btn btn-primary" onClick={() => setPostModal({})}>+ Nueva</button>
            </div>
            {posts.length === 0 ? (
              <div className="empty-state">No hay publicaciones aún.</div>
            ) : (
              <table className="admin-table">
                <thead><tr><th>Título</th><th>Categoría</th><th>Tipo</th><th>Fecha</th><th>Acciones</th></tr></thead>
                <tbody>
                  {posts.map(p => (
                    <tr key={p.id}>
                      <td>{p.title}</td>
                      <td>{p.category_name || <em style={{ color:'var(--muted)' }}>Sin categoría</em>}</td>
                      <td><span className={`badge ${p.video_url ? 'badge-video' : 'badge-gallery'}`}>{p.video_url ? 'Video' : `Galería (${p.images?.length || 0})`}</span></td>
                      <td>{new Date(p.created_at).toLocaleDateString('es-ES')}</td>
                      <td style={{ display:'flex', gap:'.5rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setPostModal(p)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePost(p.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* USUARIOS */}
        {tab === 'users' && (
          <>
            <div className="section-header">
              <h2>Usuarios</h2>
              <button className="btn btn-primary" onClick={() => setUserModal({})}>+ Nuevo</button>
            </div>
            {users.length === 0 ? (
              <div className="empty-state">No hay usuarios.</div>
            ) : (
              <table className="admin-table">
                <thead><tr><th>Usuario</th><th>Rol</th><th>Creado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td><span className={`badge ${u.role === 'admin' ? 'badge-video' : 'badge-gallery'}`}>{u.role}</span></td>
                      <td>{new Date(u.created_at).toLocaleDateString('es-ES')}</td>
                      <td style={{ display:'flex', gap:'.5rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setUserModal(u)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(u.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'categories' && <CategoriesPanel />}

        {tab === 'redes' && <RedesPanel />}

        {tab === 'settings' && <SettingsPanel onSaved={onSettingsChange} />}
      </main>

      {postModal !== null && <PostModal post={postModal} onClose={() => setPostModal(null)} onSaved={loadPosts} />}
      {userModal !== null && <UserModal user={userModal} onClose={() => setUserModal(null)} onSaved={loadUsers} />}
    </div>
  );
}
